// §7.7.4 Edge admin control surface for MedFlow staff access.
// Allows the local admin to approve/revoke individual staff accounts
// and set break-glass freeze toggles for staff sync and remote access.

import { useState, useEffect, useCallback } from 'react'
import {
  PageHeader,
  DataTable,
  StatusBadge,
  ColumnSettingsButton,
  colors,
  btnPrimary,
  btnDanger,
  card,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { getAuthToken } from '../../services/api'
import { useApiList } from '../../hooks/use-api-list'
import { useTableColumns } from '../../hooks/use-table-columns'

// ── types ─────────────────────────────────────────────────────────────────────

interface StaffRow {
  readonly id: string
  readonly email: string
  readonly name: string | null
  readonly status: 'active' | 'inactive'
}

interface StaffAccessConfig {
  readonly freezeStaffSync: boolean
  readonly freezeRemoteAccess: boolean
}

// ── helpers ───────────────────────────────────────────────────────────────────

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  return { Authorization: `Bearer ${getAuthToken()}`, ...extra }
}

// ── component ─────────────────────────────────────────────────────────────────

export function MedflowStaffAccessPage() {
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const [config, setConfig] = useState<StaffAccessConfig | null>(null)
  const [configLoading, setConfigLoading] = useState(true)
  const [configError, setConfigError] = useState<string | null>(null)
  const [togglingKey, setTogglingKey] = useState<keyof StaffAccessConfig | null>(null)

  const { tableId: _, ...tableColumnProps } = useTableColumns('medflow-staff-access')

  const {
    data: staffRows,
    loading: staffLoading,
    refresh: refreshStaff,
  } = useApiList<StaffRow>({
    path: '/admin/medflow-staff',
  })

  // Load freeze-toggle config on mount
  const fetchConfig = useCallback(async () => {
    setConfigLoading(true)
    setConfigError(null)
    try {
      const res = await fetch('/api/admin/staff-access/config', { headers: authHeaders() })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error ?? `Request failed (${res.status})`)
      }
      const body = (await res.json()) as { data: StaffAccessConfig }
      setConfig(body.data)
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to load config')
    } finally {
      setConfigLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  const handleApprove = useCallback(
    async (id: string) => {
      setActionLoading(id)
      setActionError(null)
      try {
        const res = await fetch(`/api/admin/medflow-staff/${id}/approve`, {
          method: 'POST',
          headers: authHeaders(),
        })
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string }
          throw new Error(body.error ?? `Approve failed (${res.status})`)
        }
        refreshStaff()
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to approve staff account')
      } finally {
        setActionLoading(null)
      }
    },
    [refreshStaff],
  )

  const handleRevoke = useCallback(
    async (id: string) => {
      setActionLoading(id)
      setActionError(null)
      try {
        const res = await fetch(`/api/users/${id}/local-revoke-staff`, {
          method: 'POST',
          headers: authHeaders(),
        })
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string }
          throw new Error(body.error ?? `Revoke failed (${res.status})`)
        }
        refreshStaff()
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to revoke staff account')
      } finally {
        setActionLoading(null)
      }
    },
    [refreshStaff],
  )

  const handleToggle = useCallback(
    async (key: keyof StaffAccessConfig) => {
      if (!config) return
      setTogglingKey(key)
      const newValue = !config[key]
      try {
        const res = await fetch('/api/admin/staff-access/config', {
          method: 'PUT',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ [key]: newValue }),
        })
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string }
          throw new Error(body.error ?? `Update failed (${res.status})`)
        }
        setConfig((prev) => (prev ? { ...prev, [key]: newValue } : prev))
      } catch (err) {
        setConfigError(err instanceof Error ? err.message : 'Failed to update config')
      } finally {
        setTogglingKey(null)
      }
    },
    [config],
  )

  const columns: readonly Column<StaffRow>[] = [
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      render: (row) => row.email,
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => row.name ?? '—',
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusBadge
          status={row.status === 'active' ? 'active' : 'inactive'}
          label={row.status === 'active' ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => {
        const isLoading = actionLoading === row.id
        if (row.status === 'inactive') {
          return (
            <button
              style={{ ...btnPrimary, padding: '4px 12px', fontSize: '0.8125rem' }}
              disabled={isLoading}
              onClick={(e) => {
                e.stopPropagation()
                void handleApprove(row.id)
              }}
            >
              {isLoading ? 'Approving…' : 'Approve'}
            </button>
          )
        }
        return (
          <button
            style={{ ...btnDanger, padding: '4px 12px', fontSize: '0.8125rem' }}
            disabled={isLoading}
            onClick={(e) => {
              e.stopPropagation()
              void handleRevoke(row.id)
            }}
          >
            {isLoading ? 'Revoking…' : 'Revoke'}
          </button>
        )
      },
    },
  ]

  return (
    <div>
      <PageHeader
        title="Staff Access"
        subtitle="Manage MedFlow staff accounts provisioned on this edge node"
        actions={<ColumnSettingsButton columns={columns} {...tableColumnProps} />}
      />

      <div style={{ padding: '24px' }}>
        {/* Action error banner */}
        {actionError && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {actionError}
          </div>
        )}

        {/* Staff table */}
        {staffLoading ? (
          <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading staff accounts…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={staffRows}
            keyField="id"
            emptyMessage="No MedFlow staff accounts provisioned"
            {...tableColumnProps}
          />
        )}

        {/* Freeze toggles card */}
        <div
          style={{
            ...card,
            marginTop: '32px',
            padding: '20px 24px',
          }}
        >
          <h3 style={{ margin: '0 0 4px', fontSize: '0.9375rem', color: colors.text }}>Break-Glass Controls</h3>
          <p style={{ margin: '0 0 20px', fontSize: '0.875rem', color: colors.textMuted, lineHeight: '1.5' }}>
            These toggles take effect immediately and are owned locally — they do not require central coordination.
          </p>

          {configError && (
            <div
              style={{
                marginBottom: '16px',
                padding: '10px 14px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '4px',
                color: '#dc2626',
                fontSize: '0.875rem',
              }}
            >
              {configError}
            </div>
          )}

          {configLoading ? (
            <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading config…</p>
          ) : config ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Freeze staff sync toggle */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <label
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '44px',
                    height: '24px',
                    flexShrink: 0,
                    marginTop: '2px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={config.freezeStaffSync}
                    disabled={togglingKey !== null}
                    onChange={() => {
                      void handleToggle('freezeStaffSync')
                    }}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: config.freezeStaffSync ? '#dc2626' : '#d1d5db',
                      borderRadius: '24px',
                      cursor: togglingKey !== null ? 'not-allowed' : 'pointer',
                      transition: 'background 0.2s',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: '3px',
                        left: config.freezeStaffSync ? '23px' : '3px',
                        width: '18px',
                        height: '18px',
                        background: '#fff',
                        borderRadius: '50%',
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }}
                    />
                  </span>
                </label>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: colors.text, marginBottom: '4px' }}>
                    Freeze staff sync
                    {config.freezeStaffSync && (
                      <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#dc2626', fontWeight: 500 }}>
                        ACTIVE
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: colors.textMuted, lineHeight: '1.5' }}>
                    When on, this node stops pulling new staff grants from central. Existing approved staff accounts
                    remain usable. Use this if you suspect a compromised grant or need a maintenance window.
                  </div>
                </div>
              </div>

              {/* Freeze remote access toggle */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <label
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '44px',
                    height: '24px',
                    flexShrink: 0,
                    marginTop: '2px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={config.freezeRemoteAccess}
                    disabled={togglingKey !== null}
                    onChange={() => {
                      void handleToggle('freezeRemoteAccess')
                    }}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: config.freezeRemoteAccess ? '#dc2626' : '#d1d5db',
                      borderRadius: '24px',
                      cursor: togglingKey !== null ? 'not-allowed' : 'pointer',
                      transition: 'background 0.2s',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: '3px',
                        left: config.freezeRemoteAccess ? '23px' : '3px',
                        width: '18px',
                        height: '18px',
                        background: '#fff',
                        borderRadius: '50%',
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }}
                    />
                  </span>
                </label>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: colors.text, marginBottom: '4px' }}>
                    Freeze remote access
                    {config.freezeRemoteAccess && (
                      <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#dc2626', fontWeight: 500 }}>
                        ACTIVE
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: colors.textMuted, lineHeight: '1.5' }}>
                    When on, this edge node refuses all inbound relay connections regardless of staff account status.
                    Active sessions are terminated immediately. Turn off to resume normal relay access.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Config unavailable.</p>
          )}
        </div>
      </div>
    </div>
  )
}
